import React, { FC, ReactNode } from "react";
import RefetchIcon from "@mui/icons-material/Cached";
import CloseIcon from "@mui/icons-material/Close";
import { AppBar, Box, Dialog, IconButton, Toolbar, Typography } from "@mui/material";
import { TransitionUp } from "./Transitions";

/**
 * Defines an action rendered in a FullScreenDialog top bar.
 */
interface Props {
	title: string;
	isOpen: boolean;
	isLoading: boolean; // TODO: make this flag an enum like "is loading, show, hide" or something similar and make it optional
	children: ReactNode;
	onClose: () => void;
	onReload?: () => void;
}

/**
 * FullScreenDialog renders a dialog that appears full screen and that may include some action buttons in the top bar.
 *
 * @param title
 * @param isOpen
 * @param isLoading
 * @param children
 * @param onClose
 * @param onReload
 * @constructor
 */
const FullScreenDialog: FC<Props> = ({ title, isOpen, isLoading, children, onClose, onReload }) => {
	return (
		<Dialog fullScreen open={isOpen} onClose={onClose} slots={{ transition: TransitionUp }}>
			<AppBar sx={{ position: "relative" }}>
				<Toolbar>
					{!isLoading && (
						<IconButton edge="start" color="inherit" onClick={onClose}>
							<CloseIcon />
						</IconButton>
					)}
					{isLoading && <Box sx={{ width: 28 }} />}
					<Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
						{title}
					</Typography>
					{onReload && (
						<IconButton className="top-bar-action" disabled={isLoading} onClick={onReload}>
							<RefetchIcon className={isLoading ? "rotating-icon" : undefined} />
						</IconButton>
					)}
				</Toolbar>
			</AppBar>

			<Box sx={{ p: 2 }}>{children}</Box>
		</Dialog>
	);
};

export default FullScreenDialog;
